import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SurfacePage, SoftSurface } from '../components/surface';
import CityPicker from '../components/CityPicker';
import {
  ClubPanel, ClubBackPill, PillButton, SwitchRowSoft, ErrorBand,
  MUTED, SUBTLE, INSET, TINTS, RULE, ringOn, inputStyle, textareaStyle, voiceStyle, panelTitle,
} from '../components/club/clubUi';
import clubService from '../services/clubService';
import portalService from '../services/portalService';
import useIsMobile from '../hooks/useIsMobile';

/**
 * /clubs/create — design_handoff_club_pages/README.md §6 "Create club". Header card, THE BASICS,
 * WHERE IT LIVES (GLOBAL | A TOWN + the town picker), WHO GETS IN (two visibility cards), CLUB
 * CONTROLS (three switches + member cap), and a sticky rail with the live HOW IT SHOWS UP UNDER
 * CLUBS card and CREATE CLUB. Validation and the payload are unchanged from the old form.
 *
 * Where the club lives is the same contract `ClubLocationField` keeps (global by default; naming
 * a town pins it; going global clears the town and its coords) — the segmented + picker here are
 * that field in the new vocabulary.
 */

const LOCATION_KEY = 'pr.userLocation';
function readLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const field = { fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' };
const note = { fontSize: 11, lineHeight: 1.55, color: MUTED };

export default function ClubCreatePage() {
  const navigate = useNavigate();
  const phone = useIsMobile();
  const wide = useWide();

  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    description: '',
    isPublic: true,
    maxMembers: '',
    ritual: '',
    memberSignalsEnabled: true,
    forumEnabled: false,
    anyoneCanInvite: false,
  });
  // Global by default — a club only lands in a City Portal when its founder
  // names the town, which is also what the backend defaults to.
  const [isGlobal, setIsGlobal] = useState(true);
  const [homeCity, setHomeCity] = useState(null); // { slug, label, lat, lng } | null
  const [cities, setCities] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Same public catalog the signal composer and Portal use for the nearest-city quick-pick.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await portalService.listCities?.();
      if (!cancelled && r?.success) setCities(r.data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (isSubmitting) return;
    if (!formData.name.trim()) { setError('Club name is required'); return; }
    if (!isGlobal && !homeCity) { setError('Pick the club’s town, or set it to Global'); return; }

    setIsSubmitting(true);
    setError(null);
    const payload = {
      name: formData.name.trim(),
      tagline: formData.tagline.trim() || null,
      description: formData.description.trim() || null,
      isPublic: formData.isPublic,
      maxMembers: formData.maxMembers ? parseInt(formData.maxMembers, 10) : null,
      ritual: formData.ritual.trim() || null,
      memberSignalsEnabled: formData.memberSignalsEnabled,
      forumEnabled: formData.forumEnabled,
      anyoneCanInvite: formData.anyoneCanInvite,
      isGlobal,
      locationCity: isGlobal ? null : (homeCity?.label || null),
      latitude: isGlobal ? null : (homeCity?.lat ?? null),
      longitude: isGlobal ? null : (homeCity?.lng ?? null),
    };
    const result = await clubService.createClub(payload);
    if (result?.success) {
      navigate(`/clubs/${result.data?.id}`);
    } else {
      setError(result?.error?.message || result?.error || 'Failed to create club');
    }
    setIsSubmitting(false);
  };

  const name = formData.name.trim();
  const canCreate = !!name && !isSubmitting;
  const r = phone ? 22 : 24;
  const tint = TINTS[name.length % TINTS.length];
  const previewDesc = formData.tagline.trim() || formData.description.trim().slice(0, 90) || 'Your tagline goes here.';
  const placeShort = isGlobal ? 'GLOBAL' : ((homeCity?.label || 'TOWN').split(',')[0] || 'TOWN').toUpperCase();

  const segment = (on, label, onClick) => (
    <button key={label} type="button" role="radio" aria-checked={on} onClick={onClick} className="font-mono uppercase"
      style={{ height: 40, padding: '0 18px', border: '2px solid transparent', borderRadius: 999, background: on ? ringOn : 'transparent', color: on ? '#fff' : '#000', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer' }}>
      {label}
    </button>
  );

  return (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1120, padding: phone ? '12px 12px 24px' : '24px 32px 72px', gap: phone ? 10 : 16, boxSizing: 'border-box' }} data-testid="club-create">
          <div className="flex items-center" style={{ gap: 8 }}>
            <ClubBackPill to="/clubs">CLUBS</ClubBackPill>
          </div>
          <section className="flex flex-col bg-white" style={{ gap: 8, padding: phone ? '18px 16px' : '26px 28px', borderRadius: r }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.18em', color: MUTED }}>START A CLUB</span>
            <h1 className="m-0" style={{ fontSize: phone ? 24 : 34, fontWeight: 700, lineHeight: 1.1, textTransform: 'none' }}>Turn your people into members.</h1>
            <span style={{ fontSize: 13, lineHeight: 1.6, color: MUTED }}>Free to start. You can add a price, cover photos and a forum any time after.</span>
          </section>

          <form onSubmit={submit} className="grid items-start" style={{ gridTemplateColumns: phone || !wide ? 'minmax(0,1fr)' : 'minmax(0,1fr) 340px', gap: phone ? 10 : 16 }}>
            <div className="flex min-w-0 flex-col" style={{ gap: phone ? 10 : 16 }}>
              {error && <ErrorBand testId="create-error">{error}</ErrorBand>}

              <ClubPanel phone={phone} style={{ gap: 18 }} data-testid="the-basics">
                <span style={panelTitle}>THE BASICS</span>
                <label className="flex flex-col" style={{ gap: 8 }}>
                  <span style={field}>CLUB NAME <span style={{ color: '#FF00AA' }}>*</span></span>
                  <input value={formData.name} onChange={(e) => handleChange('name', e.target.value)} maxLength={100} placeholder="Name the club" style={{ ...inputStyle, height: 52, fontSize: 16, fontWeight: 700 }} />
                </label>
                <label className="flex flex-col" style={{ gap: 8 }}>
                  <span className="flex justify-between" style={{ gap: 10 }}><span style={field}>TAGLINE</span><span style={{ fontSize: 10, color: SUBTLE }}>{formData.tagline.length}/80</span></span>
                  <input value={formData.tagline} onChange={(e) => handleChange('tagline', e.target.value)} maxLength={80} placeholder="We taste Okanagan wine like it’s a sport." style={{ ...inputStyle, height: 50, ...voiceStyle }} />
                  <span style={note}>One short line in italic under the name. Save the long story for the description.</span>
                </label>
                <label className="flex flex-col" style={{ gap: 8 }}>
                  <span className="flex justify-between" style={{ gap: 10 }}><span style={field}>DESCRIPTION</span><span style={{ fontSize: 10, color: SUBTLE }}>{formData.description.length}/500</span></span>
                  <textarea value={formData.description} onChange={(e) => handleChange('description', e.target.value)} maxLength={500} rows={4} placeholder="What’s this club about?" style={textareaStyle} />
                </label>
                <label className="flex flex-col" style={{ gap: 8 }}>
                  <span style={field}>RITUAL</span>
                  <input value={formData.ritual} onChange={(e) => handleChange('ritual', e.target.value)} maxLength={200} placeholder="First-Thursday tastings" style={{ ...inputStyle, height: 50, ...voiceStyle, fontSize: 17 }} />
                  <span style={note}>The club’s signature recurring thing. Optional.</span>
                </label>
              </ClubPanel>

              <ClubPanel phone={phone} style={{ gap: 12 }} data-testid="where-it-lives">
                <span style={panelTitle}>WHERE IT LIVES</span>
                <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
                  <span role="radiogroup" aria-label="Where the club lives" className="flex" style={{ gap: 2, padding: 3, borderRadius: 999, background: INSET }}>
                    {segment(isGlobal, 'GLOBAL', () => { setIsGlobal(true); setHomeCity(null); setError(null); })}
                    {segment(!isGlobal, 'A TOWN', () => { setIsGlobal(false); setError(null); })}
                  </span>
                </span>
                {!isGlobal && (
                  <div data-testid="town-picker">
                    <CityPicker value={homeCity} onChange={(c) => { setHomeCity(c); setError(null); }} cities={cities} nearLocation={readLocation()} />
                  </div>
                )}
                <span style={note}>{isGlobal ? 'Global clubs show up everywhere, but never in a town’s list or its City Portal.' : 'The club lands in this town’s Clubs list and its City Portal.'}</span>
              </ClubPanel>

              <ClubPanel phone={phone} style={{ gap: 12 }} data-testid="who-gets-in">
                <span style={panelTitle}>WHO GETS IN</span>
                <div className="grid" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : 'repeat(2,minmax(0,1fr))', gap: 8 }}>
                  {[['PUBLIC', true, 'Anyone can see the club and join on the spot.'], ['PRIVATE', false, 'People see the name and ask. You decide.']].map(([l, v, hint]) => {
                    const on = formData.isPublic === v;
                    return (
                      <button key={l} type="button" aria-pressed={on} onClick={() => handleChange('isPublic', v)} className="flex flex-col text-left font-mono"
                        style={{ gap: 6, padding: '18px 20px', border: '2px solid transparent', borderRadius: 20, background: on ? ringOn : INSET, color: on ? '#fff' : '#000', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
                        <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.12em' }}>{l}</span>
                        <span style={{ fontSize: 11, lineHeight: 1.55, opacity: 0.75 }}>{hint}</span>
                      </button>
                    );
                  })}
                </div>
              </ClubPanel>

              <ClubPanel phone={phone} style={{ gap: 4 }} data-testid="club-controls">
                <span style={{ ...panelTitle, paddingBottom: 8 }}>CLUB CONTROLS</span>
                <SwitchRowSoft rule={false} title="Members can open Portals" desc="Let members broadcast a short, expiring Portal to the club." on={formData.memberSignalsEnabled} onChange={(v) => handleChange('memberSignalsEnabled', v)} testId="switch-memberSignalsEnabled" />
                <SwitchRowSoft title="Turn on the club forum" desc="Threaded discussion with categories, separate from the feed." on={formData.forumEnabled} onChange={(v) => handleChange('forumEnabled', v)} testId="switch-forumEnabled" />
                <SwitchRowSoft title="Any member can invite" desc="Off means only admins can bring someone in." on={formData.anyoneCanInvite} onChange={(v) => handleChange('anyoneCanInvite', v)} testId="switch-anyoneCanInvite" />
                <div className="flex items-center" style={{ gap: 16, padding: '14px 0', borderTop: RULE }}>
                  <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>Member cap</span>
                    <span style={note}>Leave it empty for no cap.</span>
                  </span>
                  <input value={formData.maxMembers} onChange={(e) => handleChange('maxMembers', e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="NONE" aria-label="Member cap"
                    style={{ ...inputStyle, width: 96, height: 42, padding: '0 12px', textAlign: 'center', fontWeight: 700 }} />
                </div>
              </ClubPanel>
            </div>

            <div className="flex min-w-0 flex-col" style={{ gap: 10, position: phone ? 'static' : 'sticky', top: 88 }} data-testid="create-rail">
              <section className="flex flex-col bg-white" style={{ borderRadius: r, padding: 18, gap: 12 }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: MUTED }}>HOW IT SHOWS UP UNDER CLUBS</span>
                <div className="flex flex-col overflow-hidden" style={{ borderRadius: 20, background: INSET }} data-testid="card-preview">
                  <span className="relative flex items-center justify-center" style={{ aspectRatio: '16 / 9', background: tint, fontSize: 56, fontWeight: 800 }}>
                    {(name.charAt(0) || '?').toUpperCase()}
                    {!formData.isPublic && <span className="absolute" style={{ right: 10, top: 10, padding: '4px 9px', borderRadius: 999, background: '#000', color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '.12em' }}>PRIVATE</span>}
                  </span>
                  <span className="flex flex-col" style={{ padding: '14px 16px 16px', gap: 6 }}>
                    <span className="uppercase" style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.06em', lineHeight: 1.25, overflowWrap: 'anywhere' }}>{name || 'Your club name'}</span>
                    <span style={{ fontSize: 12, lineHeight: 1.5, color: '#4B5563' }}>{previewDesc}</span>
                    <span className="flex items-center justify-between" style={{ gap: 10, paddingTop: 6 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em' }}>1 MEMBER · {placeShort}</span>
                      <span className="flex items-center" style={{ height: 30, padding: '0 14px', borderRadius: 999, background: '#000', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>{formData.isPublic ? 'JOIN' : 'REQUEST'}</span>
                    </span>
                  </span>
                </div>
                <span style={note}>Cover photos come next. Once the club exists, EDIT COVER on its page lays them out.</span>
              </section>
              <PillButton type="submit" size={56} fontSize={12} tracking=".16em" disabled={!canCreate} style={{ width: '100%', opacity: canCreate || isSubmitting ? 1 : 0.4 }} data-testid="create-club">
                {isSubmitting ? 'CREATING…' : 'CREATE CLUB'}
              </PillButton>
              <span className="text-center" style={{ fontSize: 10, letterSpacing: '.1em', color: SUBTLE }}>YOU’RE THE OWNER. NOTHING IS CHARGED.</span>
            </div>
          </form>
        </main>
      </SurfacePage>
    </SoftSurface>
  );
}

/** ≥ 980px keeps the rail beside the form (README §6); narrower stacks it under. */
function useWide() {
  const [wide, setWide] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 980));
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 980);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return wide;
}
